'use client';

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Z } from '@/lib/z-index';
import { useAnimatedClose } from '@/lib/use-animated-close';
import {
	Download,
	Heart,
	Bookmark,
	Shield,
	X,
	Loader2,
	Check,
	ExternalLink,
	Tag,
	Clock,
	User,
	Terminal,
	Copy,
	BrainCircuit,
	UserCircle,
	Play,
	Layers,
} from 'lucide-react';
import type { ClawHubSkill } from '@/lib/clawhub-api';
import { skillInteraction, formatDownloads } from '@/lib/clawhub-api';
import { useToast } from '@/lib/hooks/use-toast';
import { SkillDemoSidebar } from './skill-demo-sidebar';
import { SkillPurchaseModal } from './skill-purchase-modal';
import { SKILL_DEMO_MAP } from '@/lib/skill-demo-data';
import { analytics } from '@/src/utils/analytics';

type InstallMode = 'agent' | 'human';

/** 单个互动类型的状态 */
interface InteractionState {
	active: boolean;
	loading: boolean;
	count: number;
}

function useInteraction(
	skillKey: string,
	type: 'like' | 'collect' | 'download',
	initial: { active: boolean; count: number },
	isLoggedIn: boolean,
	onLoginRequired: (() => void) | undefined,
	onSuccess?: (type: 'like' | 'collect' | 'download', active: boolean, count: number) => void,
	toast?: ReturnType<typeof useToast>,
) {
	const [state, setState] = useState<InteractionState>({
		active: initial.active,
		loading: false,
		count: initial.count,
	});
	// 外部 initial 变化时同步（例如列表页点赞后 skill prop 更新）
	useEffect(() => {
		setState({ active: initial.active, loading: false, count: initial.count });
	}, [initial.active, initial.count]);

	const toggle = useCallback(async () => {
		if (!isLoggedIn) {
			onLoginRequired?.();
			return;
		}
		const newActive = !state.active;
		setState((s) => ({ ...s, loading: true }));
		try {
			await skillInteraction({ skillKey, type, action: newActive ? 'click' : 'cancel' });
			const newCount = state.count + (newActive ? 1 : -1);
			setState({ active: newActive, loading: false, count: newCount });
			onSuccess?.(type, newActive, newCount);
			// 埋点：互动行为
			const eventMap = { like: 'SkillLike', collect: 'SkillCollect', download: 'SkillDownload' } as const;
			analytics.track(eventMap[type], {
				skill_key: skillKey,
				action: newActive ? 'add' : 'cancel',
			});
		} catch (err) {
			setState((s) => ({ ...s, loading: false }));
			if (err instanceof Error && err.message.includes('频繁')) {
				toast?.error(err.message);
			}
		}
	}, [state.active, state.count, skillKey, type, isLoggedIn, onLoginRequired, onSuccess, toast]);

	return { ...state, toggle };
}

function CopyBlock({ text, prefix }: { text: string; prefix?: string }) {
	const [copied, setCopied] = useState(false);
	const handleCopy = () => {
		navigator.clipboard.writeText(text);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
		analytics.track('SkillInstallCopy', { text });
	};
	return (
		<div className="flex items-center gap-2 bg-zinc-900 dark:bg-zinc-950 text-zinc-100 rounded-lg px-4 py-3 font-mono text-sm">
			{prefix && <span className="text-emerald-400">{prefix}</span>}
			<span className="flex-1 truncate">{text}</span>
			<button className="text-zinc-400 hover:text-zinc-200 transition-colors shrink-0" onClick={handleCopy} title="复制">
				{copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
			</button>
		</div>
	);
}

function InstallSection({ skillId, skillName }: { skillId: string; skillName: string }) {
	const [mode, setMode] = useState<InstallMode>('agent');
	const cliCommand = `npx clawhub@latest install ${skillId}`;
	const agentPrompt = `请安装并启用 "${skillName}" 技能（ID: ${skillId}），然后告诉我它能做什么。`;

	return (
		<div>
			<h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
				<Terminal className="h-3 w-3" />
				安装方式
			</h3>
			{/* Mode toggle */}
			<div className="flex items-center gap-1 mb-3 bg-secondary/50 rounded-lg p-0.5 w-fit">
				<button
					className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
						mode === 'agent' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
					}`}
					onClick={() => setMode('agent')}
				>
					<BrainCircuit className="h-3 w-3" />
					Agent 模式
				</button>
				<button
					className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
						mode === 'human' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
					}`}
					onClick={() => setMode('human')}
				>
					<UserCircle className="h-3 w-3" />
					Human 模式
				</button>
			</div>
			{mode === 'agent' ? (
				<div>
					<p className="text-caption text-muted-foreground mb-2">复制以下 Prompt 发送给你的 AI Agent：</p>
					<CopyBlock text={agentPrompt} />
				</div>
			) : (
				<div>
					<p className="text-caption text-muted-foreground mb-2">在终端运行以下命令：</p>
					<CopyBlock prefix="$" text={cliCommand} />
				</div>
			)}
		</div>
	);
}

export function SkillDetailModal({
	skill,
	onClose,
	isLoggedIn = false,
	onLoginRequired,
	onSkillChange,
}: {
	skill: ClawHubSkill;
	onClose: () => void;
	isLoggedIn?: boolean;
	onLoginRequired?: () => void;
	/** 互动操作成功后回调，返回更新后的 skill 对象供外层同步列表 */
	onSkillChange?: (updated: ClawHubSkill) => void;
}) {
	const { closing, requestClose } = useAnimatedClose(onClose, 280);
	const toast = useToast();
	const [entered, setEntered] = useState(false);
	const [showDemo, setShowDemo] = useState(false);
	const closeDemo = useCallback(() => setShowDemo(false), []);
	const toggleDemo = useCallback(() => setShowDemo((v) => !v), []);
	// 维护详情内可变的 skill 镜像（互动状态、计数随操作实时更新）
	const [localSkill, setLocalSkill] = useState<ClawHubSkill>(skill);
	const [showPurchaseModal, setShowPurchaseModal] = useState(false);
	// 外层 skill 变化时同步到 localSkill（例如列表页点赞后通知过来）
	useEffect(() => {
		setLocalSkill(skill);
	}, [skill]);
	// 跳过首次挂载，仅在互动后才通知父组件
	const isFirstRender = useRef(true);

	const handleInteractionSuccess = useCallback((type: 'like' | 'collect' | 'download', active: boolean, count: number) => {
		setLocalSkill((prev) => ({
			...prev,
			...(type === 'like' && { stars: count, isLike: active }),
			...(type === 'collect' && { collectNum: count, isCollect: active }),
			...(type === 'download' && { downloads: count, isDownload: active }),
		}));
	}, []);

	// onSkillChange 独立用 useEffect 同步，避免在 setState updater 里触发父组件 setState
	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return;
		}
		onSkillChange?.(localSkill);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [localSkill]);

	const like = useInteraction(
		localSkill.skillKey ?? localSkill.id,
		'like',
		{ active: localSkill.isLike ?? false, count: localSkill.stars },
		isLoggedIn,
		onLoginRequired,
		handleInteractionSuccess,
		toast,
	);
	const collect = useInteraction(
		localSkill.skillKey ?? localSkill.id,
		'collect',
		{ active: localSkill.isCollect ?? false, count: localSkill.collectNum ?? 0 },
		isLoggedIn,
		onLoginRequired,
		handleInteractionSuccess,
		toast,
	);
	const download = useInteraction(
		localSkill.skillKey ?? localSkill.id,
		'download',
		{ active: localSkill.isDownload ?? false, count: localSkill.downloads },
		isLoggedIn,
		onLoginRequired,
		handleInteractionSuccess,
		toast,
	);

	// Lookup demo by skill name (matches SKILL_DEMO_MAP keys)
	const demoSteps = useMemo(() => SKILL_DEMO_MAP[localSkill.name], [localSkill.name]);

	// When modal starts closing, collapse demo sidebar simultaneously
	useEffect(() => {
		if (closing) setShowDemo(false);
	}, [closing]);

	useEffect(() => {
		const raf = requestAnimationFrame(() => setEntered(true));
		return () => cancelAnimationFrame(raf);
	}, []);

	// 上报技能详情页浏览
	useEffect(() => {
		analytics.track('SkillView', {
			skill_id: skill.skillKey ?? skill.id,
			skill_name: skill.name,
			skill_category: skill.category ?? '',
			skill_is_paid: skill.isPaid === 1,
		});
	}, [skill.id, skill.skillKey, skill.name, skill.category, skill.isPaid]);

	const overlayClass = closing ? 'opacity-0 backdrop-blur-none' : entered ? 'opacity-100 backdrop-blur-xl' : 'opacity-0 backdrop-blur-none';

	const panelStyle: React.CSSProperties = {
		opacity: closing ? 0 : entered ? 1 : 0,
		transform: closing ? 'scale(0.96) translateY(8px)' : entered ? 'scale(1) translateY(0)' : 'scale(0.92) translateY(12px)',
		filter: closing ? 'blur(4px)' : entered ? 'blur(0)' : 'blur(6px)',
		transitionTimingFunction: closing ? 'cubic-bezier(0.4, 0, 1, 1)' : 'cubic-bezier(0.16, 1, 0.3, 1)',
	};

	if (typeof document === 'undefined') return null;

	const handlePurchaseSuccess = (updated: ClawHubSkill) => {
		setLocalSkill(updated);
		setShowPurchaseModal(false);
	};

	return (
		<>
			{createPortal(
				<>
					{/* Backdrop */}
					<div
						aria-hidden
						className={`liquid-glass-overlay fixed inset-0 transition-all duration-300 ease-out ${overlayClass}`}
						onClick={requestClose}
						style={{ zIndex: Z.MODAL_OVERLAY, pointerEvents: closing || !entered ? 'none' : 'auto' }}
					/>

					{/* Modal — flexbox 居中容器 */}
					<div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: Z.MODAL_CONTENT, pointerEvents: 'none' }}>
						{/* Dual-panel flex container — grows when demo sidebar is open */}
						<div
							className="flex flex-row items-stretch transition-all duration-300"
							style={{
								gap: showDemo ? 16 : 0,
								transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
							}}
						>
							{/* Demo sidebar — left of the detail panel */}
							<SkillDemoSidebar demoSteps={demoSteps} onClose={closeDemo} open={showDemo} skillName={localSkill.name} />
							<div
								className="liquid-glass-panel w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-2xl flex flex-col transition-all duration-300"
								style={{ ...panelStyle, pointerEvents: 'auto' }}
							>
								{/* Header */}
								<div className="flex items-start justify-between gap-4 border-b border-border/40 px-6 py-4">
									<div className="flex-1 min-w-0">
										<div className="flex items-center gap-2 mb-1">
											<h2 className="text-lg font-semibold text-foreground line-clamp-2">{localSkill.name}</h2>
											{localSkill.authorVerified && (
												<span className="flex items-center gap-1 text-micro font-medium text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 px-1.5 py-0.5 rounded">
													<Shield className="h-3 w-3" />
													已验证
												</span>
											)}
											{localSkill.isPaid === 1 && localSkill.isBuy ? (
												<span className="text-micro font-semibold text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/15 px-1.5 py-0.5 rounded">
													已购买
												</span>
											) : localSkill.isPaid === 1 ? (
												<span className="text-micro font-semibold text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/15 px-1.5 py-0.5 rounded">
													¥{localSkill.price?.toFixed(2) ?? '—'}
												</span>
											) : (
												<span className="text-micro font-medium text-muted-foreground bg-secondary px-1.5 py-0.5 rounded">免费</span>
											)}
										</div>
										<div className="flex items-center gap-3 text-xs text-muted-foreground">
											<span className="flex items-center gap-1">
												<User className="h-3 w-3" />
												{localSkill.author}
											</span>
											{localSkill.category && (
												<span className="flex items-center gap-1">
													<Layers className="h-3 w-3" />
													{localSkill.category}
												</span>
											)}
											<span className="flex items-center gap-1">
												<Clock className="h-3 w-3" />
												{localSkill.lastUpdated}更新
											</span>
											{localSkill.version && localSkill.version !== '—' && <span>v{localSkill.version}</span>}
										</div>
									</div>
									<button
										className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors shrink-0"
										onClick={requestClose}
										title="关闭"
									>
										<X className="h-4 w-4" />
									</button>
								</div>

								{/* Content */}
								<div className="flex-1 overflow-y-auto px-6 py-4">
									{/* Stats */}
									<div className="flex items-center gap-6 mb-5 pb-4 border-b border-border/50">
										<div className="flex items-center gap-1.5">
											<Heart className="h-4 w-4 text-rose-500" />
											<span className="text-sm font-medium text-foreground">{formatDownloads(like.count)}</span>
											<span className="text-xs text-muted-foreground">点赞</span>
										</div>
										<div className="flex items-center gap-1.5">
											<Bookmark className="h-4 w-4 text-amber-500" />
											<span className="text-sm font-medium text-foreground">{formatDownloads(collect.count)}</span>
											<span className="text-xs text-muted-foreground">收藏</span>
										</div>
										<div className="flex items-center gap-1.5">
											<Download className="h-4 w-4 text-muted-foreground" />
											<span className="text-sm font-medium text-foreground">{formatDownloads(download.count)}</span>
											<span className="text-xs text-muted-foreground">下载</span>
										</div>
									</div>

									{/* Description */}
									<div className="mb-5">
										<h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">简介</h3>
										<p className="text-sm text-foreground/85 leading-relaxed">{localSkill.description}</p>
									</div>

									{/* Tags */}
									<div className="mb-5">
										<h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
											<Tag className="h-3 w-3" />
											标签
										</h3>
										<div className="flex flex-wrap gap-2">
											{localSkill.tags.map((tag, i) => (
												<span className="text-xs bg-secondary/70 text-foreground/80 px-2.5 py-1 rounded-full" key={i}>
													{tag}
												</span>
											))}
										</div>
									</div>

									{/* Install — Agent/Human dual mode */}
									<InstallSection skillId={localSkill.id} skillName={localSkill.name} />

									{/* Links */}
									{localSkill.homepage && (
										<div className="mt-5">
											<a
												className="inline-flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400 hover:underline"
												href={localSkill.homepage}
												rel="noopener noreferrer"
												target="_blank"
											>
												<ExternalLink className="h-3 w-3" />
												查看项目主页
											</a>
										</div>
									)}
								</div>

								{/* Footer: 演示 + 互动按钮组 */}
								<div className="flex items-center justify-between border-t border-border px-6 py-4 bg-secondary/20">
									<button
										className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
											showDemo ? 'bg-secondary text-foreground' : 'bg-secondary/60 text-muted-foreground hover:bg-secondary hover:text-foreground'
										}`}
										onClick={toggleDemo}
									>
										<Play className={`h-3.5 w-3.5 ${showDemo ? 'text-primary' : ''}`} />
										{showDemo ? '关闭演示' : '查看演示'}
									</button>

									{/* 互动按钮组 */}
									<div className="flex items-center gap-2">
										{/* 点赞 */}
										<button
											className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
												like.active
													? 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20'
													: 'bg-secondary/60 text-muted-foreground hover:bg-secondary hover:text-foreground'
											} disabled:opacity-60`}
											disabled={like.loading}
											onClick={like.toggle}
											title={isLoggedIn ? (like.active ? '取消点赞' : '点赞') : '登录后点赞'}
										>
											{like.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Heart className={`h-4 w-4 ${like.active ? 'fill-current' : ''}`} />}
											<span>{formatDownloads(like.count)}</span>
										</button>

										{/* 收藏 */}
										<button
											className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
												collect.active
													? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20'
													: 'bg-secondary/60 text-muted-foreground hover:bg-secondary hover:text-foreground'
											} disabled:opacity-60`}
											disabled={collect.loading}
											onClick={collect.toggle}
											title={isLoggedIn ? (collect.active ? '取消收藏' : '收藏') : '登录后收藏'}
										>
											{collect.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bookmark className={`h-4 w-4 ${collect.active ? 'fill-current' : ''}`} />}
											<span>{formatDownloads(collect.count)}</span>
										</button>

										{/* 安装/购买 */}
										{localSkill.isPaid === 1 && !localSkill.isBuy && !download.active ? (
											<button
												className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-all bg-amber-500 text-white hover:bg-amber-600"
												onClick={() => setShowPurchaseModal(true)}
											>
												<Download className="h-4 w-4" />¥{localSkill.price?.toFixed(2) ?? '—'} 购买
											</button>
										) : (
											<button
												className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-all ${
													download.active
														? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
														: 'bg-primary text-primary-foreground hover:bg-primary/90'
												} disabled:opacity-70`}
												disabled={download.loading}
												onClick={download.toggle}
											>
												{download.loading ? (
													<Loader2 className="h-4 w-4 animate-spin" />
												) : download.active ? (
													<Check className="h-4 w-4" />
												) : (
													<Download className="h-4 w-4" />
												)}
												{download.active ? '已安装' : '安装技能'}
											</button>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>
				</>,
				document.body,
			)}

			{/* Purchase Modal for paid skills */}
			<SkillPurchaseModal isOpen={showPurchaseModal} onClose={() => setShowPurchaseModal(false)} onSuccess={handlePurchaseSuccess} skill={localSkill} />
		</>
	);
}
